"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const focalTestimonials = [{
  quote: "El tratamiento en MS Instituto Capilar superó mis expectativas con",
  highlight: "resultados naturales y un trato humano",
  quoteEnd: "que me devolvió la confianza en mi imagen.",
  name: "Carlos R.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_carlos.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_carlos.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_carlos.png-wvc-sizes"
}, {
  quote: "La profesionalidad de la Dra. Soto es evidente. Un proceso",
  highlight: "meticuloso, transparente y sin dolor",
  quoteEnd: "que marca la diferencia en el sector.",
  name: "Elena M.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_elena.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_elena.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_elena.png-wvc-sizes"
}, {
  quote: "Cada sesión fue una experiencia de excelencia. El equipo",
  highlight: "cuida cada detalle con precisión",
  quoteEnd: "y un enfoque médico impecable.",
  name: "Javier G.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_javier.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_javier.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_javier.webp-wvc-sizes"
}, {
  quote: "Un centro de referencia. La atención personalizada y",
  highlight: "la honestidad en el diagnóstico",
  quoteEnd: "hacen que te sientas en las mejores manos.",
  name: "Sofía L.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_sofia.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_sofia.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_sofia.png-wvc-sizes"
}];
const smallTestimonials = [{
  quote: "Un espacio de calma y rigor médico. Resultados que hablan por sí solos.",
  name: "Marcos T.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_marcos.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_marcos.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_marcos.png-wvc-sizes"
}, {
  quote: "La Dra. Soto explica todo con claridad. Un proceso muy cómodo y seguro.",
  name: "Lucía V.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_lucia.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_lucia.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_lucia.webp-wvc-sizes"
}, {
  quote: "Calidad humana y técnica. Mi experiencia ha sido excelente desde el inicio.",
  name: "Andrés P.",
  role: "Paciente",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_andres.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_andres.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701322/img/paciente_andres.png-wvc-sizes"
}];
export default function HistoriasDeCambioReal() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const active = focalTestimonials[activeIndex];
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? focalTestimonials.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setActiveIndex(prev => prev === focalTestimonials.length - 1 ? 0 : prev + 1);
  };
  const fullQuote = `${active.quote} ${active.highlight} ${active.quoteEnd}`;
  const words = fullQuote.split(" ");
  const highlightWords = active.highlight.split(" ");
  const highlightStart = active.quote.split(" ").length;
  const highlightEnd = highlightStart + highlightWords.length;
  return <section data-section-id="4213" id="historias-de-cambio-real" className="text-foreground py-24 md:py-32 lg:py-[140px] overflow-hidden light bg-muted">
      <div ref={sectionRef} className="container mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <span className="font-default tracking-[0.2em] uppercase text-primary !font-bold !text-xl">TESTIMONIOS</span>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-6 relative ![margin:0px_0px_80px_0px]">
          <div className="col-span-12 md:col-start-3 md:col-span-8 relative">
            {}
            <span aria-hidden="true" className="absolute -top-16 md:-top-20 lg:-top-24 -left-4 md:-left-12 lg:-left-16 font-serif text-[10rem] md:text-[14rem] lg:text-[18rem] leading-none text-chart-3 select-none pointer-events-none" style={{
            color: "var(--chart-3)"
          }}>
              &ldquo;
            </span>

            {}
            <blockquote className="relative font-default italic text-3xl md:text-5xl lg:text-[56px] leading-[1.15] text-foreground tracking-tight">
              {words.map((word, i) => {
              const isHighlight = i >= highlightStart && i < highlightEnd;
              return <span key={`${activeIndex}-${i}`} data-index={i} className="inline-block transition-all duration-700 ease-out !text-3xl" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transitionDelay: `${i * 60}ms`,
                color: isHighlight ? "var(--primary)" : undefined
              }}>
                    {word}&nbsp;
                  </span>;
            })}
            </blockquote>

            {}
            <div className="flex items-center gap-5 transition-all duration-700 ease-out ![margin:30px_0px_0px_0px]" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: `${words.length * 60 + 200}ms`
          }}>
              <SafeHydrate><Avatar className="size-16 border border-border">
                <AvatarImage src={active.avatar} alt={active.name} className="object-cover" />
                <AvatarFallback className="font-default text-base">
                  {active.name.charAt(0)}
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <div className="flex flex-col gap-1">
                <span className="font-default text-lg text-foreground">
                  {active.name}
                </span>
                <span className="font-default text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  — {active.role}
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-0 col-span-1">
            <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Testimonio anterior" className="size-12 text-muted-foreground hover:text-primary hover:bg-accent">
              <ChevronLeft className="size-6" strokeWidth={1.25} />
            </Button>
          </div>
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-0 col-span-1 justify-end">
            <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Siguiente testimonio" className="size-12 text-muted-foreground hover:text-primary hover:bg-accent">
              <ChevronRight className="size-6" strokeWidth={1.25} />
            </Button>
          </div>
        </div>

        {}
        <div className="flex justify-center gap-3 ![margin:0px_0px_80px_0px]">
          {focalTestimonials.map((_, i) => <Button key={i} data-index={i} variant="ghost" onClick={() => setActiveIndex(i)} aria-label={`Ir al testimonio ${i + 1}`} className="p-2 h-auto w-auto">
              <span className="block size-2 transition-all duration-300" style={{
            backgroundColor: i === activeIndex ? "var(--primary)" : "var(--chart-3)",
            opacity: i === activeIndex ? 1 : 0.3,
            width: i === activeIndex ? "1.5rem" : "0.5rem"
          }} />
            </Button>)}
        </div>

        {}
        <div className="flex md:hidden justify-center gap-4 mb-20">
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Testimonio anterior" className="size-12 border border-border text-muted-foreground hover:text-primary">
            <ChevronLeft className="size-5" strokeWidth={1.25} />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Siguiente testimonio" className="size-12 border border-border text-muted-foreground hover:text-primary">
            <ChevronRight className="size-5" strokeWidth={1.25} />
          </Button>
        </div>

        {}
        
      </div>
    </section>;
}